// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { PageHead, Loading } from '../../components/shell/ScreenHelpers';
import CoverageSummary from '../../components/sop/CoverageSummary';
import CoverageBySite from '../../components/sop/CoverageBySite';
import UnboundEquipmentList from '../../components/sop/UnboundEquipmentList';
import SopRegisterTable, { SopRegisterTiles } from '../../components/sop/SopRegisterTable';
import UnavailablePanel from '../../components/sop/UnavailablePanel';
import { useSopGovernance } from '../../hooks/useSopGovernance';
import type { CoverageResult, SiteCoverageRow } from '../../types/sopGovernance';
import './SopGovernance.css';

/** Named once: both the coverage and the gaps section rest on the same silence. */
const BINDING_UNPUBLISHED =
  'The ontology models a hasSOP attribute on equipment, but no environment publishes it — ' +
  'the twin returned equipment with no binding data attached. Coverage is unmeasured, ' +
  'which is not the same as 0%.';

/**
 * Flatten either coverage state onto the one row shape the table draws. A null
 * `bound` / `pct` / `outstanding` is "not published" — the site and its
 * equipment count are still real, so the row is still drawn.
 */
function siteCoverageRows(coverage: CoverageResult): SiteCoverageRow[] {
  if (coverage.state === 'measured') {
    return coverage.rollup.sites.map(site => ({
      siteId: site.siteId,
      label: site.label,
      total: site.total,
      bound: site.bound,
      pct: site.pct,
      outstanding: site.outstanding,
    }));
  }
  return coverage.sites.map(site => ({
    siteId: site.siteId,
    label: site.label,
    total: site.total,
    bound: null,
    pct: null,
    outstanding: null,
  }));
}

/**
 * Fleet › SOP governance. Binding coverage across the fleet, the equipment that
 * has no bound SOP, and the SOP register.
 *
 * Every figure comes from an upstream contract — Digital Twin equipment
 * entities for binding, the SOP Factory catalogue for the register. Nothing is
 * seeded. Where a contract is silent this says so rather than showing a number
 * nobody measured; the PR description lists each gap and what would close it.
 */
export default function SopGovernance() {
  const {
    site,
    coverage,
    coverageLoading,
    coverageError,
    register,
    registerLoading,
    registerError,
    registerTruncated,
  } = useSopGovernance();

  return (
    <div className="sg-page">
      <PageHead
        title="SOP governance"
        actions={
          <span className="sg-scope" data-testid="sop-scope">
            {site ? site.label : 'No site selected'}
          </span>
        }
      />
      {/* The binding's copy, re-scoped: its screen was fleet-wide, this one is
          the selected site. It still promises a third thing not shown yet —
          procedures that can't be bound to the Knowledge Graph, the binding's
          `covUnbindable` tile. No contract serves it. */}
      <p className="sg-blurb">
        Binding coverage for this site: which active equipment has a bound SOP, which gaps remain,
        and which procedures can&rsquo;t be bound to the Knowledge Graph.
      </p>

      {/* Coverage ----------------------------------------------------------
          Every section below keeps the shape the binding gave it in both states.
          Unpublished binding leaves the panels, the site rows and the gap
          heading in place and names the gap where each figure would be — the
          screen is also the map of what still needs wiring up. `UnavailablePanel`
          is reserved for a hard failure, where there is no shape to draw. */}
      {!site && (
        <UnavailablePanel
          title="No site selected"
          detail="SOP binding coverage is measured per site. Pick a site from fleet overview to see which of its equipment has a bound SOP; the register below spans every procedure in the catalog."
        />
      )}

      {site && coverageLoading && <Loading label="Loading coverage…" />}

      {site && !coverageLoading && coverageError && (
        <UnavailablePanel
          title="Coverage unavailable"
          detail={`The Digital Twin did not return a graph for ${site?.label ?? 'this site'}.`}
        />
      )}

      {site && !coverageLoading && !coverageError && coverage && (
        <>
          {coverage.state === 'unpublished' && (
            <UnavailablePanel title="Binding coverage not published" detail={BINDING_UNPUBLISHED} />
          )}

          <CoverageSummary coverage={coverage} />

          {/* One row now the screen is one site, but it is the only place the
              outstanding-by-equipment-type breakdown appears. */}
          <h2 className="sg-heading">Coverage by site</h2>
          <CoverageBySite sites={siteCoverageRows(coverage)} />

          <div className="sg-heading-row">
            {/* The binding's heading. Its note reads `{n} open · {m} resolved`;
                there is no resolve endpoint, so the resolved half is dropped
                rather than hard-coded to zero. */}
            <h2 className="sg-heading">Open compliance gaps</h2>
            <span className="sg-heading-note">
              {coverage.state === 'measured'
                ? `${coverage.rollup.unbound.length} open`
                : 'not measured'}
            </span>
          </div>
          <UnboundEquipmentList
            items={coverage.state === 'measured' ? coverage.rollup.unbound : null}
          />
        </>
      )}

      {/* SOP register ------------------------------------------------------ */}
      <h2 className="sg-heading">SOP register</h2>

      {registerLoading && <Loading label="Loading the SOP register…" />}

      {!registerLoading && registerError && (
        <UnavailablePanel
          title="Register unavailable"
          detail="The SOP Factory catalog could not be reached."
        />
      )}

      {!registerLoading && !registerError && register.length === 0 && (
        <UnavailablePanel
          title="No procedures returned"
          detail="The SOP Factory catalog returned no SOP artifacts for this fleet."
        />
      )}

      {!registerLoading && !registerError && register.length > 0 && (
        <>
          {registerTruncated && (
            <p className="sg-heading-note" role="status">
              Only the first {register.length} SOPs are shown; the catalog holds more.
            </p>
          )}
          <SopRegisterTiles rows={register} />
          <SopRegisterTable rows={register} />
        </>
      )}
    </div>
  );
}
